import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionHead } from "@/components/apex/primitives";

const H_MIN = 2;
const H_MAX = 9;
const W_MIN = 1.4;
const W_MAX = 2.5;

const Dimensions = () => {
  const { t } = useLang();
  const [h, setH] = useState(4.5);
  const [w, setW] = useState(1.9);

  const frameH = 320;
  const doorH = 70 + ((h - H_MIN) / (H_MAX - H_MIN)) * (frameH - 90);
  const doorW = 46 + ((w - W_MIN) / (W_MAX - W_MIN)) * 78;

  return (
    <section data-testid="dimensions-section" className="relative bg-[var(--cream-2)] py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead label={t.dimensions.label} title={t.dimensions.title} />

        <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="space-y-4">
              {[
                { id: "dim-h", label: t.dimensions.heightLabel, v: h, set: setH, min: H_MIN, max: H_MAX, step: 0.1, a: "2.00 m", b: "9.00 m", aria: t.dimensions.sliderHeight, tid: "dim-height" },
                { id: "dim-w", label: t.dimensions.widthLabel, v: w, set: setW, min: W_MIN, max: W_MAX, step: 0.01, a: "1.40 m", b: "2.50 m", aria: t.dimensions.sliderWidth, tid: "dim-width" },
              ].map((s) => (
                <div key={s.id} className="ap-card p-7">
                  <div className="flex items-baseline justify-between gap-4">
                    <label htmlFor={s.id} className="text-[14px] font-semibold text-[var(--text)]">
                      {s.label}
                    </label>
                    <span className="ap-display text-3xl text-[var(--text)]" dir="ltr" data-testid={`${s.tid}-value`}>
                      {s.v.toFixed(2)} <span className="text-base text-[var(--muted)]">{t.dimensions.meter}</span>
                    </span>
                  </div>
                  <input
                    id={s.id}
                    type="range"
                    min={s.min}
                    max={s.max}
                    step={s.step}
                    value={s.v}
                    onChange={(e) => s.set(parseFloat(e.target.value))}
                    aria-label={s.aria}
                    data-testid={`${s.tid}-slider`}
                    className="ap-range mt-7"
                  />
                  <div className="ap-mono mt-3 flex justify-between text-[12px] text-[var(--muted-2)]" dir="ltr">
                    <span>{s.a}</span>
                    <span>{s.b}</span>
                  </div>
                </div>
              ))}

              <p className="ap-card p-6 text-[14.5px] leading-relaxed text-[var(--text)]">{t.dimensions.config}</p>
              <p className="text-[13.5px] leading-relaxed text-[var(--muted)]">{t.dimensions.note}</p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <figure className="ap-card p-8 sm:p-10">
              <svg
                viewBox="0 0 300 370"
                className="h-auto w-full"
                role="img"
                aria-label={`${t.dimensions.illustrative}: ${h.toFixed(2)}${t.dimensions.meter} × ${w.toFixed(2)}${t.dimensions.meter}`}
                data-testid="dimensions-illustration"
              >
                <g stroke="#151515" strokeOpacity="0.06" strokeWidth="1">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <line key={i} x1={40 + i * 44} y1="10" x2={40 + i * 44} y2="340" />
                  ))}
                </g>
                <line x1="20" y1="340" x2="290" y2="340" stroke="#151515" strokeOpacity="0.25" />
                <rect
                  x={150 - doorW / 2}
                  y={340 - doorH}
                  width={doorW}
                  height={doorH}
                  rx="7"
                  fill="#F5C518"
                  fillOpacity="0.25"
                  stroke="#9A7A00"
                  strokeWidth="1.5"
                  style={{ transition: "all 500ms cubic-bezier(0.16,1,0.3,1)" }}
                />
                <line
                  x1={150 - doorW / 2 + doorW * 0.8}
                  y1={340 - doorH * 0.6}
                  x2={150 - doorW / 2 + doorW * 0.8}
                  y2={340 - doorH * 0.3}
                  stroke="#151515"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ transition: "all 500ms cubic-bezier(0.16,1,0.3,1)" }}
                />
                <g stroke="#9A7A00" strokeOpacity="0.7" strokeWidth="1" fill="none">
                  <line x1="34" y1={340 - doorH} x2="34" y2="340" />
                  <line x1="28" y1={340 - doorH} x2="40" y2={340 - doorH} />
                  <line x1="28" y1="340" x2="40" y2="340" />
                  <line x1={150 - doorW / 2} y1="356" x2={150 + doorW / 2} y2="356" />
                  <line x1={150 - doorW / 2} y1="350" x2={150 - doorW / 2} y2="362" />
                  <line x1={150 + doorW / 2} y1="350" x2={150 + doorW / 2} y2="362" />
                </g>
                <text
                  x="24"
                  y={340 - doorH / 2}
                  fill="#151515"
                  fontSize="10"
                  fontWeight="600"
                  textAnchor="middle"
                  transform={`rotate(-90 24 ${340 - doorH / 2})`}
                  fontFamily="Plus Jakarta Sans, sans-serif"
                >
                  {h.toFixed(2)} m
                </text>
                <text x="150" y="346" fill="#151515" fontSize="10" fontWeight="600" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif">
                  {w.toFixed(2)} m
                </text>
              </svg>
              <figcaption className="mt-6 text-center text-[12.5px] text-[var(--muted)]">{t.dimensions.illustrative}</figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Dimensions;
